import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Users, 
  BarChart3, 
  Trophy, 
  Target,
  Calendar,
  TrendingUp,
  UserCheck,
  Activity,
  Award,
  Book,
  Database,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import OddunManagement from './OddunManagement';
import ContributionManagement from './ContributionManagement';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total_users: 0,
    total_games_started: 0,
    total_games_completed: 0,
    completion_rate: 0
  });
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    username: '',
    full_name: '',
    password: '',
    language: 'es'
  });
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showOddunManagement, setShowOddunManagement] = useState(false);
  const [showContributionManagement, setShowContributionManagement] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [usersResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUsers(usersResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error al cargar datos de administrador');
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await axios.put(
        `${API}/admin/users/${selectedUser.id}/password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Contraseña actualizada para ${selectedUser.email}`);
      setShowPasswordReset(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      const message = error.response?.data?.detail || 'Error al cambiar la contraseña';
      toast.error(message);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Usuario ${userToDelete.email} eliminado exitosamente`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      
      // Refresh user list
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting user:', error);
      const message = error.response?.data?.detail || 'Error al eliminar el usuario';
      toast.error(message);
    }
  };

  const handlePasswordReset = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordReset(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'babalawo': return 'bg-purple-100 text-purple-800';
      case 'santero': return 'bg-blue-100 text-blue-800';
      case 'student': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, 
        { new_role: newRole },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { new_role: newRole }
        }
      );
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      
      toast.success(`Rol actualizado a ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Error al actualizar el rol del usuario');
    }
  };

  const createAdminUser = async () => {
    if (!newAdminData.email || !newAdminData.username || !newAdminData.full_name || !newAdminData.password) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    setIsCreatingAdmin(true);
    try {
      await axios.post(`${API}/admin/users/create-admin`, newAdminData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Usuario administrador creado exitosamente');
      setShowCreateAdmin(false);
      setNewAdminData({
        email: '',
        username: '',
        full_name: '',
        password: '',
        language: 'es'
      });
      
      // Refresh user list
      fetchAdminData();
    } catch (error) {
      console.error('Error creating admin user:', error);
      const message = error.response?.data?.detail || 'Error al crear el usuario administrador';
      toast.error(message);
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  const getLanguageFlag = (language) => {
    switch (language) {
      case 'es': return '🇪🇸';
      case 'en': return '🇺🇸';
      case 'fr': return '🇫🇷';
      default: return '🌐';
    }
  };

  if (showOddunManagement) {
    return <OddunManagement onBack={() => setShowOddunManagement(false)} />;
  }

  if (showContributionManagement) {
    return <ContributionManagement onBack={() => setShowContributionManagement(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium text-lg">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  const overviewCards = [
    {
      title: 'Total de Usuarios',
      value: stats.total_users,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100',
      change: '+12%'
    },
    {
      title: 'Juegos Iniciados',
      value: stats.total_games_started,
      icon: Target,
      color: 'text-emerald-600',
      bg: 'bg-emerald-100',
      change: '+8%'
    },
    {
      title: 'Juegos Completados',
      value: stats.total_games_completed,
      icon: Trophy,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
      change: '+15%'
    },
    {
      title: 'Tasa de Finalización',
      value: `${Math.round(stats.completion_rate)}%`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
      change: '+5%'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Panel de Administración</h1>
              <p className="text-emerald-600">Gestiona usuarios y supervisa el rendimiento de la plataforma</p>
            </div>
          </div>
          
          <div className="achievement-badge">
            <Award className="w-5 h-5" />
            Administrador
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {overviewCards.map((card, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <span className="text-emerald-600 text-sm font-semibold">{card.change}</span>
              </div>
              <p className="text-2xl font-bold text-emerald-800 mb-1">{card.value}</p>
              <p className="text-gray-600 text-sm">{card.title}</p>
            </div>
          ))}
        </div>

        {/* Users Table - Full Width */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-emerald-600" />
                <h2 className="font-cinzel text-xl font-bold text-emerald-800">Usuarios Registrados</h2>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-emerald-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Rol</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Nivel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Registro</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-emerald-700 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userData, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center mr-3">
                            <span className="text-white font-semibold text-sm">
                              {userData.full_name ? userData.full_name.charAt(0).toUpperCase() : userData.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{userData.full_name || userData.username}</div>
                            <div className="text-sm text-gray-500">{userData.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={userData.role}
                          onChange={(e) => updateUserRole(userData.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${getRoleColor(userData.role)}`}
                        >
                          <option value="student">Estudiante</option>
                          <option value="santero">Santero</option>
                          <option value="babalawo">Babalawo</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm text-gray-900">{userData.level}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(userData.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {userData.is_active ? (
                            <div className="flex items-center text-green-600">
                              <UserCheck className="w-4 h-4 mr-1" />
                              <span className="text-sm">Activo</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-gray-400">
                              <Activity className="w-4 h-4 mr-1" />
                              <span className="text-sm">Inactivo</span>
                            </div>
                          )}
                          <span className="text-lg">{getLanguageFlag(userData.language)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePasswordReset(userData)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                            title="Cambiar contraseña"
                          >
                            🔑 Cambiar Contraseña
                          </button>
                          <button
                            onClick={() => handleDeleteUser(userData)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                            title="Eliminar usuario"
                            disabled={userData.id === user.id}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay usuarios registrados</p>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Platform Stats */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-6 h-6 text-emerald-600" />
              <h3 className="font-cinzel text-lg font-bold text-emerald-800">Estadísticas</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Usuarios Totales</span>
                <span className="font-bold text-emerald-800">{stats.total_users}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Juegos Iniciados</span>
                <span className="font-bold text-emerald-800">{stats.total_games_started}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Juegos Completados</span>
                <span className="font-bold text-emerald-800">{stats.total_games_completed}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tasa de Finalización</span>
                <span className="font-bold text-emerald-800">{Math.round(stats.completion_rate)}%</span>
              </div>
            </div>
          </div>

          {/* User Demographics */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-4">Demografía de Usuarios</h3>
              
              <div className="space-y-3">
                {['es', 'en', 'fr'].map(lang => {
                  const count = users.filter(u => u.language === lang).length;
                  const percentage = users.length > 0 ? (count / users.length * 100).toFixed(1) : 0;
                  
                  return (
                    <div key={lang} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getLanguageFlag(lang)}</span>
                        <span className="text-gray-600">
                          {lang === 'es' ? 'Español' : lang === 'en' ? 'English' : 'Français'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-emerald-800">{count}</span>
                        <span className="text-xs text-gray-500 ml-1">({percentage}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Admin User Creation */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-4">Crear Usuario Admin</h3>
              
              {!showCreateAdmin ? (
                <button
                  onClick={() => setShowCreateAdmin(true)}
                  className="w-full bg-emerald-500 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 hover:bg-emerald-600 flex items-center justify-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  Nuevo Administrador
                </button>
              ) : (
                <div className="space-y-4">
                  <input
                    type="email"
                    placeholder="Email"
                    value={newAdminData.email}
                    onChange={(e) => setNewAdminData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <input
                    type="text"
                    placeholder="Nombre de usuario"
                    value={newAdminData.username}
                    onChange={(e) => setNewAdminData(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <input
                    type="text"
                    placeholder="Nombre completo"
                    value={newAdminData.full_name}
                    onChange={(e) => setNewAdminData(prev => ({ ...prev, full_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <input
                    type="password"
                    placeholder="Contraseña"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <select
                    value={newAdminData.language}
                    onChange={(e) => setNewAdminData(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                  </select>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={createAdminUser}
                      disabled={isCreatingAdmin}
                      className="flex-1 bg-emerald-500 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 hover:bg-emerald-600 disabled:opacity-50"
                    >
                      {isCreatingAdmin ? 'Creando...' : 'Crear'}
                    </button>
                    <button
                      onClick={() => setShowCreateAdmin(false)}
                      className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 hover:bg-gray-600"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-primary rounded-2xl p-6 text-white shadow-lg">
              <h3 className="font-cinzel text-lg font-bold mb-4">Acciones Rápidas</h3>
              
              <div className="space-y-3">
                <button 
                  onClick={() => setShowOddunManagement(true)}
                  className="w-full bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Database className="w-5 h-5" />
                  Gestión de Odduns
                </button>
                
                <button 
                  onClick={() => setShowContributionManagement(true)}
                  className="w-full bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  Gestión de Contribuciones
                </button>
                
                <button 
                  onClick={fetchAdminData}
                  className="w-full bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Activity className="w-5 h-5" />
                  Actualizar Datos
                </button>
                
                <Link 
                  to="/dashboard"
                  className="block w-full bg-yellow-500 hover:bg-yellow-600 text-emerald-800 px-4 py-3 rounded-xl font-semibold transition-all duration-300 text-center"
                >
                  Volver al Dashboard
                </Link>
              </div>
            </div>
            

          </div>

        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center mb-6">
                <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-2">
                  Cambiar Contraseña
                </h3>
                <p className="text-emerald-600">
                  Usuario: {selectedUser?.email}
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => {
                    setShowPasswordReset(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={resetUserPassword}
                  disabled={isResettingPassword || newPassword.length < 6}
                  className="flex-1 bg-blue-500 text-white py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResettingPassword ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Cambiando...
                    </div>
                  ) : (
                    'Cambiar Contraseña'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h3 className="font-cinzel text-2xl font-bold text-red-800 mb-2">
                  Confirmar Eliminación
                </h3>
                <p className="text-gray-600 mb-4">
                  ¿Estás seguro de que quieres eliminar la cuenta de:
                </p>
                <p className="font-semibold text-red-700">
                  {userToDelete?.email}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Esta acción no se puede deshacer. Se eliminarán todos los datos del usuario.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={deleteUser}
                  className="flex-1 bg-red-500 text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition-colors"
                >
                  Sí, Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;